<template>
  <div>
    <el-row type="flex" class="title-bar" justify="space-between">
      <div class="title-h3">무선 AP</div>
      <div>
        <el-button size="small" @click="onClickExportExcel"><img src="@/assets/img/icons/excel.png" style="position:relative;top:3px;left:-2px" /> 엑셀출력</el-button>
        <w-header-search-button @click="clickShowSearchTool"></w-header-search-button>
      </div>
    </el-row>
    <el-table-wrapper
      v-loading="isLoading"
      ref="wirelessApTable"
      :data="tableData"
      :columns="wirelessApColumns"
      :show-custom-header="true"
      :show-search-tool="showSearchTool"
      :total="total"
      :remote="true"
      :column-default="columnDefault"
      pagination
      @condition-change="onTableConditionChange"
    >
      <template #obj-name-slot="scope">
        <span v-if="scope.row.objName" class="link c-pointer" @click="clickObjName(scope.row)">{{scope.row.objName}}</span>
        <span v-else>-</span>
      </template>
    </el-table-wrapper>
  </div>
</template>

<script>
import { isNil, isEmpty } from 'lodash';
import { columnDefault } from '@/constants/table';
import msgUtil from '@/utils/message';
import { routePathByObjId } from '@/utils/router';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import wirelessDeviceInfoAPI from '@/api/config/wireless-device/wireless-device-info';
import fileApi from '@/api/common/file';
import { menuDefine } from '@/constants/menu';
import { performanceNodeTabs } from '@/constants/performance';

export default {
  name: 'WWirelessApNode',
  props: {
    activeTab: {
      type: String,
    },
    nodeParams: {
      type: Object,
    },
  },
  data() {
    return {
      columnDefault,
      showSearchTool: false,
      tableData: null,
      total: null,
      isLoading: false,
      params: {
        objId: null,
      },
      wirelessApColumns: [
        {
          prop: 'objName',
          label: '장비 명',
          scopedSlot: 'obj-name-slot',
          remoteProp: 'o.obj_name',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'ipaddress',
          label: 'IP',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'objMacaddress',
          label: 'MAC',
          remoteProp: 'o.obj_macaddress',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'objCompanyName',
          label: '제조사',
          remoteProp: 'oc.obj_company_name',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'objProductId',
          label: '모델명',
          remoteProp: 'o.obj_product_id',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'ssid',
          label: 'WLAN',
          remoteProp: 'ncmInfo.ssid',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'radioBand',
          label: '주파수 대역',
          remoteProp: 'ncmInfo.radioBand',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'apGroup',
          label: 'AP 그룹',
          remoteProp: 'ncmInfo.apGroup',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'osVersion',
          label: 'OS 버전',
          remoteProp: 'ncmInfo.osVersion',
          searchable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'netdeviceUptimeCount',
          label: '구동 시간',
          searchable: false,
          formatter: this.convertNetdeviceUptime,
        },
      ],
    };
  },
  watch: {
    activeTab: {
      handler() {
        this.params.objId = this.nodeParams.objId;
        this.loadData();
      },
    },
    nodeParams: {
      handler() {
        this.params.objId = this.nodeParams.objId;
        this.loadData();
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * 컨트롤러에 속한 무선 AP 목록을 조회한다.
     * @author yelim
     * @return {[type]} [description]
     */
    loadData() {
      if (this.activeTab === this.$options.name) {
        const tableParams = this.$refs.wirelessApTable.getParameter();
        const params = Object.assign(tableParams, this.params);
        this.isLoading = true;
        wirelessDeviceInfoAPI
          .getApControllerConfigApList(params)
          .then((response) => {
            if (response.data.success) {
              this.tableData = response.data.data;
              this.total = response.data.total;
            } else {
              this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '컨트롤러 무선 AP 정보', false));
            }
          })
          .catch((e) => {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '컨트롤러 무선 AP 정보', false));
            throw new Error(e);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    onTableConditionChange() {
      this.loadData();
    },
    clickShowSearchTool() {
      this.showSearchTool = !this.showSearchTool;
    },
    getTableValueAfterCheckEmpty(row, column, value) {
      return getValueAfterCheckEmpty(value);
    },
    /**
     * 컨트롤러에 속한 무선 AP 목록을 엑셀 출력한다.
     * @author yelim
     * @return {[type]} [description]
     */
    onClickExportExcel() {
      const tableParams = this.$refs.wirelessApTable.getParameter();
      const params = Object.assign(tableParams, this.params);
      this.isLoading = true;
      wirelessDeviceInfoAPI
        .exportApControllerConfigApList(params)
        .then((response) => {
          if (response.data.success && response.data.data) {
            fileApi.downloadExportedExcelFile(response.data.data);
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.OUTPUT, '컨트롤러 무선 AP 정보', false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.OUTPUT, '컨트롤러 무선 AP 정보', false));
          throw new Error(e);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    convertNetdeviceUptime(row, column, value) {
      if (isNil(value)) {
        return EMPTY_TEXT;
      }
      return `${value}일`;
    },
    clickObjName(row) {
      if (!isNil(row.objId)) {
        routePathByObjId(this, row.objId, menuDefine.PERF_OPER, performanceNodeTabs.summary.name, isEmpty(this.selectedNode) ? undefined : this.selectedNode.childManagegroupIds, null, false);
      }
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/views/config/node/ap-controller/wirelessap/index.vue